import { compact } from 'lodash';
import * as z from 'zod';

import { USStrictAddress } from '../../Address';
import { PayeeInput, PayeePaymentMethod, PayeeTaxType, PayeeType } from '../../payee/Payee';
import { OptionalPhoneNumber } from '../../Phone';
import {
  ADDRESS_FIELD_KEYS,
  FLAT_FIELD_BY_ADDRESS_KEY,
  isAddressBlank,
  optionalDueMonthDay,
  optionalMiCompanyCode,
  optionalPostcode,
  optionalString,
  optionalTaxOption,
  preprocessTrim,
  rowToAddress,
} from './payeeRowPreprocessors';

// Update row: only externalPayeeId is required (the lookup key). All other fields are
// optional; only fields present on the row are applied as a patch onto the existing payee.
export const BulkUpdatePayeesRow = z
  .object({
    externalPayeeId: z.preprocess(preprocessTrim, z.string().min(1)),
    name: z.preprocess(preprocessTrim, z.string().min(1).optional()),
    payeeType: PayeeType.optional(),
    taxType: PayeeTaxType.optional(),
    taxOption: optionalTaxOption,
    miCompanyCode: optionalMiCompanyCode,
    paymentMethod: PayeePaymentMethod.optional(),
    phone: OptionalPhoneNumber,
    addressLine1: optionalString,
    addressLine2: optionalString,
    addressLocality: optionalString,
    addressRegion: optionalString,
    addressPostcode: optionalPostcode,
    addressCountry: optionalString,
    dueDate1: optionalDueMonthDay,
    dueDate2: optionalDueMonthDay,
    dueDate3: optionalDueMonthDay,
    dueDate4: optionalDueMonthDay,
    isActive: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // When the row sets payeeType, mirror the create-row cross-field rules so the patch
    // applied downstream is internally consistent. When payeeType is absent, defer the
    // tax/non-tax checks to the processor (which sees the merged payee).
    if (data.payeeType === 'TAX') {
      // taxType/taxOption/dueDates may still be omitted to "leave existing values
      // unchanged"; nothing extra to require here.
    } else if (data.payeeType !== undefined) {
      if (data.taxType !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Only allowed for tax payees' });
      }
      if (data.taxOption !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['taxOption'], message: 'Only allowed for tax payees' });
      }
      const dueDateKeys = ['dueDate1', 'dueDate2', 'dueDate3', 'dueDate4'] as const;
      for (const key of dueDateKeys) {
        if (data[key]) {
          ctx.addIssue({ code: 'custom', path: [key], message: 'Only allowed for tax payees' });
        }
      }
    }

    if (data.payeeType !== undefined && data.payeeType !== 'MORTGAGE_INSURANCE' && data.miCompanyCode !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['miCompanyCode'],
        message: 'Only allowed for mortgage insurance payees',
      });
    }

    const anyAddressFilled = ADDRESS_FIELD_KEYS.some((key) => (data[key] ?? '').trim().length > 0);
    if (anyAddressFilled) {
      const result = USStrictAddress.safeParse(rowToAddress(data));
      if (!result.success) {
        for (const issue of result.error.issues) {
          const top = issue.path[0];
          const flatKey = typeof top === 'string' ? FLAT_FIELD_BY_ADDRESS_KEY[top] : undefined;
          ctx.addIssue({ ...issue, path: flatKey ? [flatKey] : issue.path });
        }
      }
    }
  });

export type BulkUpdatePayeesRow = z.infer<typeof BulkUpdatePayeesRow>;

export const BULK_UPDATE_PAYEES_ROW_REQUIRED_FIELDS: ReadonlyArray<keyof BulkUpdatePayeesRow> = ['externalPayeeId'];

// All fields except externalPayeeId; a non-undefined value indicates "set this field".
export type BulkUpdatePayeesRowPatch = Partial<Omit<PayeeInput, 'externalPayeeId'>>;

// Builds a patch of fields explicitly set on the row. Fields omitted from the row stay
// undefined here, meaning "do not change". Address fields collapse together — present
// only if any one is non-blank.
export const bulkUpdatePayeesRowToPayeeInputPatch = (row: BulkUpdatePayeesRow): BulkUpdatePayeesRowPatch => {
  const patch: BulkUpdatePayeesRowPatch = {};

  if (row.name !== undefined) patch.name = row.name;
  if (row.payeeType !== undefined) patch.payeeType = row.payeeType;
  if (row.taxType !== undefined) patch.taxType = row.taxType;
  if (row.taxOption !== undefined) patch.taxOption = row.taxOption;
  if (row.miCompanyCode !== undefined) patch.miCompanyCode = row.miCompanyCode;
  if (row.paymentMethod !== undefined) patch.paymentMethod = row.paymentMethod;
  if (row.phone !== undefined) patch.phone = row.phone || undefined;
  if (row.isActive !== undefined) patch.isActive = row.isActive;

  if (!isAddressBlank(row)) {
    patch.address = rowToAddress(row);
  }

  const dueDateValues = [row.dueDate1, row.dueDate2, row.dueDate3, row.dueDate4];
  const anyDueDateProvided = dueDateValues.some((d) => d !== undefined);
  if (anyDueDateProvided) {
    patch.dueDates = compact(dueDateValues).filter((d): d is string => d !== '');
  }

  return patch;
};
